/** @format */
import React, { useEffect, useState, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "js-cookie";

const API_URL = process.env.REACT_APP_FRONTEND_URL;

// 🔹 Avatar helpers
const avatarBgClass = (seed = "") => {
  const palette = [
    "bg-sky-500",
    "bg-indigo-500",
    "bg-emerald-500",
    "bg-rose-500",
    "bg-amber-500",
    "bg-teal-500",
    "bg-fuchsia-500",
    "bg-cyan-500",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

const getInitials = (fullName = "") => {
  const tokens = String(fullName)
    .replace(/[^\p{L}\p{N}\s'-]/gu, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "??";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  const first = tokens[0][0] || "";
  const last = tokens[tokens.length - 1][0] || "";
  return (first + last).toUpperCase();
};

const AssignEmployeeLeave = () => {
  const token = Cookies.get("user_token");

  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showPopup, setShowPopup] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);

  const [currentLeaveCount, setCurrentLeaveCount] = useState("");
  const [actualLeaveCount, setActualLeaveCount] = useState("");
  const [selectedLeaveCategory, setSelectedLeaveCategory] = useState("");
  const [leaveCount, setLeaveCount] = useState("");
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // 🔹 Fetch all leave categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/hris/leave/getLeaveCategory`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok)
          throw new Error(json?.message || "Failed to fetch categories");
        setLeaveCategories(json.data || []);
      } catch (err) {
        toast.error(err.message);
      }
    };
    fetchCategories();
  }, [token]);

  const fetchEmployees = useCallback(
    async (keyword = "", page = 1) => {
      setLoading(true);
      setLoadError("");
      try {
        const params = new URLSearchParams({
          page,
          limit: pageSize,
        });
        if (keyword) params.append("search", keyword);

        const res = await fetch(
          `${API_URL}/v1/hris/leave/employee-category?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const json = await res.json();

        if (!res.ok || !json.success)
          throw new Error(json?.message || "Failed to fetch employees");

        const employeesData = json.data || [];

        // ✅ Dynamically compute total pages if not returned by API
        const totalCount =
          json.total || json.totalCount || json.count || employeesData.length;
        const totalPages =
          json.totalPages || Math.max(1, Math.ceil(totalCount / pageSize));
        const currentPageFromApi = json.page || page;

        setEmployees(employeesData);
        setTotal(totalCount);
        setTotalPages(totalPages);
        setCurrentPage(currentPageFromApi);
      } catch (err) {
        setLoadError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    },
    [token, pageSize]
  );

  // 🔹 Initial fetch
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // 🔹 Debounce search input (auto fetch after 600 ms)
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchEmployees(search.trim(), 1);
    }, 600);
    return () => clearTimeout(timeout);
  }, [search, fetchEmployees]);

  // 🔹 Popup open & close
  const closePopup = () => {
    setShowPopup(false);
    setSelectedCategory(null);
  };
  const closeAddPopup = () => {
    setShowAddPopup(false);
    setSelectedEmployee(null);
    setSelectedLeaveCategory("");
    setLeaveCount("");
  };

  // 🔹 Handle leave category click (view / edit)
  const handleCategoryClick = async (category, emp) => {
    setSelectedCategory({ ...category, employee_no: emp.employee_no });
    setShowPopup(true);
    setIsEditMode(false);
    try {
      const res = await fetch(
        `${API_URL}/v1/hris/leave/employee-category-count?employee_no=${emp.employee_no}&leave_category_id=${category.category_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok)
        throw new Error(json?.message || "Failed to fetch leave balance");
      setLeaveBalance(json.data || null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSaveAddLeave = async () => {
    if (!selectedEmployee || !selectedLeaveCategory || !leaveCount) {
      toast.error("Please fill all fields before saving");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        employee_no: selectedEmployee.employee_no,
        leave_category_id: selectedLeaveCategory,
        leave_count: Number(leaveCount),
      };

      const res = await fetch(
        `${API_URL}/v1/hris/leave/addEmployeeLeaveCount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.message || "Failed to assign leave category");
      }

      toast.success("Leave category assigned successfully");
      closeAddPopup();
      // Optional: Refresh employee list
      fetchEmployees(search, currentPage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };
  // 🔹 Add new leave category (fetch organization-specific categories)
  const handleAddLeaveClick = async (emp) => {
    console.log("Selected employee →", emp); // ✅ Add this

    setSelectedEmployee(emp);
    setSelectedLeaveCategory("");
    setLeaveCount("");
    setSelectedCategoryDetails(null);
    setShowAddPopup(true);

    try {
      // 🧩 Fetch leave categories for this employee's organization
      const res = await fetch(
        `${API_URL}/v1/hris/leave/getLeaveCategory?employee_no=${emp.employee_no}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();

      if (!res.ok || !json.success)
        throw new Error(json?.message || "Failed to fetch leave categories");

      // ✅ Set only organization-matched categories
      setLeaveCategories(json.data || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // 🔹 Update leave counts
  const handleSave = async () => {
    if (!selectedCategory) return;
    setIsSaving(true);

    try {
      const payload = {
        actual_leave_counts: [
          {
            leave_category_id: selectedCategory.category_id,
            leave_count: Number(actualLeaveCount),
          },
        ],
        current_leave_counts: [
          {
            leave_category_id: selectedCategory.category_id,
            leave_count: Number(currentLeaveCount),
          },
        ],
      };

      const res = await fetch(
        `${API_URL}/v1/hris/leave/update-leave-count?employee_no=${selectedCategory.employee_no}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();
      if (!res.ok)
        throw new Error(json?.message || "Failed to update leave counts");

      toast.success("Leave counts updated successfully");
      closePopup();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="font-montserrat mt-5">
      <p className="text-[25px] mb-4 ">
        Assign Employee to Leaves
      </p>

      {/* 🔍 Auto Search Field */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by Employee No or Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-2 w-80"
        />
      </div>

      {/* 🧑‍💼 Employee Table */}
      <div className="overflow-x-auto bg-white shadow-md rounded-md">
        <table className="w-full border-collapse text-left text-sm text-gray-600">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3">Employee No</th>
              <th className="px-6 py-3">Employee</th>
              <th className="px-6 py-3">Assigned Leave Categories</th>
              <th className="px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td colSpan="4" className="py-4 text-center text-red-600">
                  {loadError}
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-4 text-center text-gray-400">
                  No employees found.
                </td>
              </tr>
            ) : (
              employees.map((emp) => (
                <tr key={emp.employee_no} className="hover:bg-gray-50">
                  <td className="px-6 py-3">{emp.employee_no}</td>
                  <td className="px-6 py-3 flex items-center gap-3">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full text-white font-semibold ${avatarBgClass(
                        emp.employee_name
                      )}`}
                    >
                      {getInitials(emp.employee_name)}
                    </div>
                    <div>
                      <div className="font-medium">{emp.employee_name}</div>
                      <div className="text-xs text-gray-500">
                        {emp.employee_email || "N/A"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    {emp.assigned_leave_categories?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {emp.assigned_leave_categories.map((cat) => (
                          <span
                            key={cat.category_id}
                            className="px-2 py-1 rounded bg-green-100 text-green-600 text-xs cursor-pointer hover:bg-green-200"
                            onClick={() => handleCategoryClick(cat, emp)}
                          >
                            {cat.category_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">
                        No categories
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      onClick={() => handleAddLeaveClick(emp)}
                      className="text-blue-600 hover:text-blue-400"
                    >
                      + Add
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm text-gray-700">
        <div>
          Showing {(currentPage - 1) * pageSize + 1}–
          {Math.min(currentPage * pageSize, total)} of {total} results
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={currentPage <= 1 || loading}
            onClick={() => fetchEmployees(search, currentPage - 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage >= totalPages || loading}
            onClick={() => fetchEmployees(search, currentPage + 1)}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* 🧾 Leave Count Popup */}
      {showPopup && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-[480px]">
            <h2 className="text-lg font-semibold text-center mb-4">
              {selectedCategory.category_name}
            </h2>

            {leaveBalance && !isEditMode ? (
              <div className="bg-gray-50 p-3 rounded mb-4">
                <p>
                  <strong>Current Leave:</strong>{" "}
                  {leaveBalance.current_leave_count}
                </p>
                <p>
                  <strong>Actual Leave:</strong>{" "}
                  {leaveBalance.actual_leave_count}
                </p>
                <p>
                  <strong>NoPay:</strong> {leaveBalance.nopay}
                </p>
              </div>
            ) : (
              isEditMode && (
                <div className="flex gap-3 mb-3">
                  <input
                    type="number"
                    className="border rounded p-2 w-full"
                    placeholder="Current Leave Count"
                    value={currentLeaveCount}
                    onChange={(e) => setCurrentLeaveCount(e.target.value)}
                  />
                  <input
                    type="number"
                    className="border rounded p-2 w-full"
                    placeholder="Actual Leave Count"
                    value={actualLeaveCount}
                    onChange={(e) => setActualLeaveCount(e.target.value)}
                  />
                </div>
              )
            )}

            <div className="flex justify-end gap-2">
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => setIsEditMode(false)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                >
                  Edit
                </button>
              )}
              <button
                onClick={closePopup}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ➕ Add Category Popup */}
      {showAddPopup && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-[480px]">
            <h2 className="text-lg font-semibold mb-4 text-center">
              Add Leave Category
            </h2>

            <p className="text-sm text-gray-700 mb-2 font-medium">
              Employee: {selectedEmployee.employee_name} (
              {selectedEmployee.employee_no})
            </p>

            <select
              value={selectedLeaveCategory || ""}
              onChange={(e) => {
                const categoryId = Number(e.target.value);
                setSelectedLeaveCategory(categoryId);
                const details = leaveCategories.find(
                  (c) => c.id === categoryId
                );
                setSelectedCategoryDetails(details || null);
                if (details) setLeaveCount(details.no_of_days);
              }}
              className="w-full border rounded p-2 mb-3"
            >
              <option value="">-- Select Leave Category --</option>
              {leaveCategories.map((cat) => {
                const alreadyAssigned =
                  selectedEmployee.assigned_leave_categories?.some(
                    (a) => a.category_id === cat.id
                  );
                return (
                  <option
                    key={cat.id}
                    value={cat.id}
                    disabled={alreadyAssigned}
                  >
                    {cat.category_name}
                    {alreadyAssigned ? " (Assigned)" : ""}
                  </option>
                );
              })}
            </select>

            {selectedCategoryDetails && (
              <div className="bg-gray-50 p-2 rounded mb-3 text-sm">
                <p>Eligible Days: {selectedCategoryDetails.no_of_days}</p>
                <p>
                  Recurring:{" "}
                  {selectedCategoryDetails.annual_recurring ? "Yes" : "No"}
                </p>
                <p>Gender: {selectedCategoryDetails.eligible_gender}</p>
              </div>
            )}

            <input
              type="number"
              placeholder="Leave Count"
              className="w-full border rounded p-2 mb-3"
              value={leaveCount}
              onChange={(e) => setLeaveCount(e.target.value)}
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={closeAddPopup}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAddLeave}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AssignEmployeeLeave;
